import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoggerService } from './logger.service';

/** What a stalled request turned out to be, once we probed the network. */
export type ConnectionVerdict = 'network-unreachable' | 'network-reachable';

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Short mixed alphanumerics — license plates (`6LPS470`) and similar bare identifiers. */
const IDENTIFIER_SEGMENT = /^(?=.*\d)[A-Za-z0-9]{5,10}$/;

/**
 * Reduce a request URL to just its endpoint shape for logging.
 *
 * These log lines can ship to a third-party sink, and the raw URLs carry customer data: license
 * plates sit in the path (`/vehicle/plate/CA/6LPS470`) and location and lane ids appear in both
 * the path and the query string. The endpoint shape is all the telemetry needs.
 */
export function redactUrlForTelemetry(rawUrl: string): string {
  let pathname: string;
  try {
    // Base only matters for relative URLs; it is never read back.
    pathname = new URL(rawUrl, 'https://redacted.invalid').pathname;
  } catch {
    return '(unparseable url)';
  }
  return pathname
    .split('/')
    .map(segment =>
      UUID_SEGMENT.test(segment) || IDENTIFIER_SEGMENT.test(segment) ? ':id' : segment
    )
    .join('/');
}

/** Resource Timing detail for a single request, when the browser will give it to us. */
export interface ConnectionTrace {
  /**
   * True when the request went out over an already-pooled connection (no DNS/TCP/TLS work).
   * This is the fingerprint of the stale-connection failure: a reused socket that the network
   * silently killed accepts the write and then never answers.
   * Null when the browser withheld the detail (cross-origin without Timing-Allow-Origin).
   */
  reusedConnection: boolean | null;
  /** 'h3' | 'h2' | 'http/1.1', or null when withheld. */
  protocol: string | null;
}

/**
 * Tells "this endpoint is slow" apart from "our network is gone".
 *
 * The tablets sit behind carrier NAT; an idle connection can be reaped without a FIN or RST,
 * so a request written to it hangs until the OS gives up (~35-40s observed) instead of failing.
 * Elapsed time alone cannot distinguish that from a genuinely slow query, so we ask the network
 * directly with a cheap round trip.
 */
@Injectable({ providedIn: 'root' })
export class ConnectionHealthService {
  /** Short by design: the probe is on the critical path for detecting a dead network. */
  private static readonly PROBE_TIMEOUT_MS = 2000;

  /**
   * A response that just arrived is itself proof the path is alive, so a probe inside this window
   * is answered from that instead of spending a request. Keeps normal operation, where slow-but-
   * healthy calls are common, from generating probe traffic.
   */
  private static readonly RECENT_SUCCESS_WINDOW_MS = 2000;

  private inFlightProbe: Promise<ConnectionVerdict> | null = null;
  private lastSuccessAt = 0;

  /** Called for every successful HTTP response. */
  markSuccess(): void {
    this.lastSuccessAt = Date.now();
  }

  constructor(private readonly logger: LoggerService) {}

  /**
   * Cheap round trip to the API. Any HTTP answer at all — including 401/403 — proves the path
   * is alive, so only a transport-level failure counts as unreachable. Concurrent callers share
   * one probe, since a burst of stalled requests is exactly the case this exists for.
   */
  probe(): Promise<ConnectionVerdict> {
    if (Date.now() - this.lastSuccessAt < ConnectionHealthService.RECENT_SUCCESS_WINDOW_MS) {
      return Promise.resolve('network-reachable');
    }
    if (this.inFlightProbe) {
      return this.inFlightProbe;
    }
    const probe = this.runProbe().finally(() => {
      this.inFlightProbe = null;
    });
    this.inFlightProbe = probe;
    return probe;
  }

  private async runProbe(): Promise<ConnectionVerdict> {
    if (typeof fetch === 'undefined') {
      return 'network-reachable';
    }

    const controller = new AbortController();
    const abortTimer = setTimeout(
      () => controller.abort(),
      ConnectionHealthService.PROBE_TIMEOUT_MS
    );

    try {
      await fetch(`${environment.baseUrl}/${environment.apiVersion}/app/version`, {
        method: 'GET',
        // Must bypass the HTTP cache: a cached 200 would report a healthy path without a packet
        // ever leaving the device.
        cache: 'no-store',
        signal: controller.signal,
      });
      return 'network-reachable';
    } catch {
      return 'network-unreachable';
    } finally {
      clearTimeout(abortTimer);
    }
  }

  /**
   * Resource Timing for the most recent request to `url`. All-zero timings mean the browser
   * withheld the detail rather than that the connection was reused, so report that as unknown.
   */
  describeConnection(url: string): ConnectionTrace {
    const unknown: ConnectionTrace = { reusedConnection: null, protocol: null };
    if (typeof performance === 'undefined' || !performance.getEntriesByType) {
      return unknown;
    }

    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const entry = entries.filter(candidate => candidate.name.startsWith(url)).pop();
    if (!entry) {
      return unknown;
    }

    const protocol = entry.nextHopProtocol || null;
    if (entry.connectEnd === 0 && entry.requestStart === 0) {
      return { reusedConnection: null, protocol };
    }

    const reusedConnection =
      entry.connectEnd === entry.connectStart && entry.domainLookupEnd === entry.domainLookupStart;
    return { reusedConnection, protocol };
  }

  /**
   * Record a request that blew its timeout budget. Today this is a structured log line; it is the
   * signal a dashboard should alert on, since this failure is otherwise only visible by hand-reading
   * a session replay.
   */
  recordStalledRequest(context: {
    method: string;
    url: string;
    budgetMs: number;
    verdict: ConnectionVerdict;
  }): void {
    // Resource Timing is matched on the real URL; only the logged copy is redacted.
    const trace = this.describeConnection(context.url);
    this.logger.warn(
      `Request exceeded its ${context.budgetMs}ms budget (${context.verdict})`,
      {
        method: context.method,
        endpoint: redactUrlForTelemetry(context.url),
        budgetMs: context.budgetMs,
        verdict: context.verdict,
        reusedConnection: trace.reusedConnection,
        protocol: trace.protocol,
      },
      'ConnectionHealthService'
    );
  }
}
